import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, UtensilsCrossed, CalendarPlus, History, LogOut,
  Menu, X, Shield, FileText, Dumbbell, ShieldCheck, Pill, FlaskConical,
  ArrowLeftRight, Stethoscope, File, BarChart3, Users, ChevronRight,
  Mic, Activity, Moon, Sun, Brain, Settings
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useTheme } from "./ThemeProvider";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ee59bb71-25b5-4f3e-ade6-7b45b4f149b6/artifacts/v205dw0b_IMG_7633.png";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { path: "/patients", label: "Patients", icon: Users },
    ],
  },
  {
    label: "Clinical",
    items: [
      { path: "/notes", label: "AI Scribe", icon: Stethoscope },
      { path: "/labs", label: "Patient Labs", icon: FlaskConical },
      { path: "/documents", label: "Documents", icon: File },
      { path: "/activity", label: "Activity Log", icon: Activity },
    ],
  },
  {
    label: "Nutrition",
    items: [
      { path: "/create-plan", label: "Create Meal Plan", icon: CalendarPlus },
      { path: "/history", label: "Plan History", icon: History },
      { path: "/compare", label: "Compare Plans", icon: ArrowLeftRight },
      { path: "/templates", label: "Templates", icon: FileText },
    ],
  },
  {
    label: "Libraries",
    items: [
      { path: "/recipes", label: "Recipes", icon: UtensilsCrossed },
      { path: "/exercises", label: "Exercises", icon: Dumbbell },
      { path: "/supplements", label: "Supplements", icon: Pill },
    ],
  },
  {
    label: "Account",
    items: [
      { path: "/settings", label: "Settings", icon: Settings },
    ],
  },
];

const ADMIN_GROUP = {
  label: "Administration",
  items: [
    { path: "/admin", label: "Admin Panel", icon: ShieldCheck, isAdmin: true },
    { path: "/analytics", label: "Analytics", icon: BarChart3, isAdmin: true },
    { path: "/ai-learning", label: "AI Learning", icon: Brain, isAdmin: true },
  ],
};

// NavItem extracted as a top-level component to prevent unmount/remount on parent re-renders.
// When defined inline inside Sidebar, React treats it as a NEW component type on every render,
// causing the DOM element to be replaced — which loses in-flight touch/click events on mobile.
const NavItem = ({ item, compact, isActive, onNav }) => {
  const Icon = item.icon;
  return (
    <button
      onClick={() => onNav(item.path)}
      data-testid={`nav-${item.path.replace("/", "")}`}
      className={`
        w-full flex items-center gap-3 rounded-lg font-medium text-sm
        transition-all duration-150 border-0 cursor-pointer bg-transparent text-left
        ${compact ? "px-3 py-2" : "px-4 py-2.5"}
        ${isActive
          ? "bg-forest/15 text-emerald-400 font-semibold"
          : item.isAdmin
            ? "text-amber-400/80 hover:bg-white/5 hover:text-amber-300 active:bg-white/10"
            : "text-slate-400 hover:bg-white/5 hover:text-slate-200 active:bg-white/10"
        }
      `}
    >
      <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? "text-emerald-400" : item.isAdmin && !isActive ? "text-amber-500/70" : "text-slate-500"}`} />
      <span className="flex-1">{item.label}</span>
      {item.isAdmin && (
        <span className="text-[9px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded-full font-bold tracking-wide">ADMIN</span>
      )}
      {isActive && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
    </button>
  );
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { dark, toggle: toggleTheme } = useTheme();

  const groups = user?.role === "admin"
    ? [ADMIN_GROUP, ...NAV_GROUPS]
    : NAV_GROUPS;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer is open on mobile
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMobile, isOpen]);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const handleNav = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  // ========== MOBILE: Slide-out drawer ==========
  if (isMobile) {
    return (
      <>
        {/* Top bar */}
        <div className="fixed top-0 left-0 right-0 z-50 lg:hidden">
          <header className="bg-slate-900/95 backdrop-blur-md border-b border-slate-800 px-4 py-2.5 flex items-center justify-between">
            <button onClick={() => handleNav("/dashboard")} className="flex items-center gap-2 bg-transparent border-0 cursor-pointer p-0">
              <img src={LOGO_URL} alt="Logo" className="w-8 h-8 object-contain" />
              <span className="font-heading font-bold text-emerald-400 text-sm">Nutrition Solutions</span>
            </button>
            <div className="flex items-center gap-1.5">
              {/* Quick action: Start scribe */}
              <button
                onClick={() => handleNav("/notes?autostart=true")}
                className="h-9 w-9 flex items-center justify-center rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 active:scale-95 transition-colors border-0 cursor-pointer"
                data-testid="mobile-quick-scribe"
              >
                <Mic className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                data-testid="mobile-menu-btn"
                className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-slate-800 active:bg-slate-700 bg-transparent border-0 cursor-pointer transition-colors"
              >
                {isOpen ? <X className="w-5 h-5 text-slate-400" /> : <Menu className="w-5 h-5 text-slate-400" />}
              </button>
            </div>
          </header>
        </div>

        {/* Backdrop */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-[60] lg:hidden animate-in fade-in duration-200"
            onClick={() => setIsOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
        )}

        {/* Slide-out drawer */}
        <div
          className={`fixed top-0 left-0 bottom-0 w-[280px] bg-slate-900 z-[70] lg:hidden
            transform transition-transform duration-300 ease-out shadow-2xl
            ${isOpen ? "translate-x-0" : "-translate-x-full"}
          `}
          data-testid="mobile-drawer"
        >
          <div className="flex flex-col h-full">
            {/* Drawer header */}
            <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <img src={LOGO_URL} alt="Logo" className="w-9 h-9 object-contain" />
                <div>
                  <h2 className="font-heading font-bold text-emerald-400 text-sm leading-tight">Nutrition Solutions</h2>
                  <p className="text-[10px] text-slate-500 font-medium">For Me</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-slate-800 bg-transparent border-0 cursor-pointer"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            {/* Scrollable nav */}
            <nav className="flex-1 overflow-y-auto overscroll-contain py-3 px-3 space-y-4" data-testid="mobile-nav-scroll">
              {groups.map((group) => (
                <div key={group.label}>
                  <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest px-3 mb-1.5">{group.label}</p>
                  <div className="space-y-0.5">
                    {group.items.map((item) => (
                      <NavItem key={item.path} item={item} isActive={location.pathname === item.path} onNav={handleNav} />
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            {/* HIPAA badge */}
            <div className="px-4 py-2 mx-3 mb-2 bg-emerald-950/50 rounded-lg border border-emerald-900/50">
              <div className="flex items-center gap-2 text-emerald-400">
                <Shield className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="text-[10px] font-semibold">HIPAA Compliant</span>
              </div>
            </div>

            {/* User section */}
            <div className="p-4 border-t border-slate-800">
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="h-9 w-9 flex-shrink-0">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-emerald-900/50 text-emerald-400 font-semibold text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-200 truncate">{user?.name}</p>
                  <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start text-slate-500 hover:text-red-400 hover:bg-red-950/50 text-xs h-9"
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-3.5 h-3.5 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ========== DESKTOP: Fixed sidebar ==========
  return (
    <aside
      className="hidden lg:flex w-[260px] bg-slate-900 border-r border-slate-800 h-screen fixed left-0 top-0 z-40 flex-col transition-colors duration-200"
      data-testid="sidebar"
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-slate-800">
          <button onClick={() => handleNav("/dashboard")} className="flex items-center gap-3 bg-transparent border-0 cursor-pointer p-0">
            <img src={LOGO_URL} alt="Nutrition Solutions" className="w-10 h-10 object-contain" />
            <div className="text-left">
              <h1 className="font-heading font-bold text-emerald-400 text-sm leading-tight tracking-tight">Nutrition Solutions</h1>
              <p className="text-[10px] text-slate-500 font-medium tracking-wide">For Me</p>
            </div>
          </button>
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto overscroll-contain py-4 px-3 space-y-5" data-testid="desktop-nav-scroll">
          {groups.map((group) => (
            <div key={group.label}>
              <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest px-3 mb-2">{group.label}</p>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavItem key={item.path} item={item} compact isActive={location.pathname === item.path} onNav={handleNav} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* HIPAA badge */}
        <div className="px-3 py-2 mx-3 mb-2 bg-emerald-950/40 rounded-lg border border-emerald-900/50">
          <div className="flex items-center gap-2 text-emerald-400">
            <Shield className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="text-[10px] font-semibold">HIPAA Compliant</span>
          </div>
          <p className="text-[9px] text-emerald-500/50 mt-0.5 pl-5">PHI encrypted & secured</p>
        </div>

        {/* User */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-1 mb-3">
            <Avatar className="h-9 w-9 flex-shrink-0">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="bg-emerald-900/50 text-emerald-400 font-semibold text-xs">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">{user?.name}</p>
              <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-500 hover:text-red-400 hover:bg-red-950/50 text-xs h-9"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>
    </aside>
  );
}
